// app/frontend/components/ui/spacing.ts
/**
 * L2K Spacing Guide
 * Consistent spacing values for mobile and desktop layouts
 *
 * Usage:
 * - Import SPACING and use with useMediaQuery to get responsive values
 * - Or use the helper functions for common patterns
 */

/**
 * Base spacing scale (in Mantine spacing units)
 * xs: 10px, sm: 12px, md: 16px, lg: 20px, xl: 24px
 */
export const SPACING = {
  // Page-level padding
  page: {
    mobile: "sm",      // 12px
    desktop: "md",     // 16px
  },

  // Section gaps (between major sections)
  section: {
    mobile: "lg",      // 20px
    desktop: "xl",     // 24px
  },

  // Card padding
  card: {
    mobile: "md",      // 16px
    desktop: "xl",     // 24px
  },

  // Form gaps (between form fields)
  form: {
    mobile: "sm",      // 12px
    desktop: "md",     // 16px
  },

  // Stack gaps (general vertical spacing)
  stack: {
    tight: {
      mobile: "xs",    // 10px
      desktop: "sm",   // 12px
    },
    normal: {
      mobile: "sm",    // 12px
      desktop: "md",   // 16px
    },
    loose: {
      mobile: "md",    // 16px
      desktop: "lg",   // 20px
    },
  },

  // Group gaps (horizontal spacing)
  group: {
    tight: {
      mobile: "xs",    // 10px
      desktop: "xs",   // 10px
    },
    normal: {
      mobile: "sm",    // 12px
      desktop: "md",   // 16px
    },
  },

  // Header/Navbar padding
  header: {
    mobile: {
      py: "xs",        // 10px vertical
      px: "sm",        // 12px horizontal
    },
    desktop: {
      py: "md",        // 16px vertical
      px: "xl",        // 24px horizontal
    },
  },
} as const;

/**
 * Text size recommendations for mobile vs desktop
 * These align with Mantine's size props (xs, sm, md, lg, xl)
 */
export const TEXT_SIZES = {
  // Body text
  body: {
    mobile: "sm",      // 14px
    desktop: "md",     // 16px
  },

  // Labels (form labels, etc)
  label: {
    mobile: "xs",      // 12px
    desktop: "sm",     // 14px
  },

  // Captions/helper text
  caption: {
    mobile: "xs",      // 12px
    desktop: "xs",     // 12px (same on both)
  },

  // Buttons
  button: {
    mobile: "xs",      // smaller buttons on mobile
    desktop: "md",     // normal buttons on desktop
  },

  // Input fields
  input: {
    mobile: "sm",      // smaller inputs on mobile
    desktop: "md",     // normal inputs on desktop
  },
} as const;

/**
 * Helper to get responsive spacing value
 * @param spacing - The spacing object with mobile/desktop keys
 * @param isMobile - Whether current viewport is mobile
 */
export function getResponsiveSpacing<T extends { mobile: string; desktop: string }>(
  spacing: T,
  isMobile: boolean
): string {
  return isMobile ? spacing.mobile : spacing.desktop;
}

/**
 * Breakpoint for mobile detection
 * Use with useMediaQuery: const isMobile = useMediaQuery("(max-width: 768px)")
 */
export const MOBILE_BREAKPOINT = 768;
export const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;
